// index.js
// Reference citation plugin - shows citation numbers as superscript
// Follows ProseMirror best practices with clean separation of concerns

import {Plugin, PluginKey} from "prosemirror-state";
import {applyToPluginState, initPluginState} from './pluginState';
import {createPluginView} from './pluginView';

export const referenceCitationPluginKey = new PluginKey("referenceCitation");

/**
 * Reference Citation Plugin
 *
 * Displays citation numbers as superscript at the end of reference marks.
 * Numbers are calculated based on document order from the reference number store.
 *
 * ProseMirror Best Practices:
 * - Pure state management in plugin.state (no side effects)
 * - Side effects isolated in plugin.view
 * - Efficient decoration updates via change detection
 * - Proper cleanup on destroy
 * - Stable object identity where possible
 *
 * @param {Object} options - Plugin options
 * @param {string} options.reportId - Report ID for fetching citation numbers
 * @returns {Plugin} ProseMirror plugin
 */
export function referenceCitationPlugin(options = {}) {
    const {reportId, yReferenceMarks} = options;

    return new Plugin({
        key: referenceCitationPluginKey,

        state: {
            init(_, state) {
                return initPluginState(state, reportId);
            },

            apply(tr, pluginState, _oldState, newState) {
                return applyToPluginState(tr, pluginState, newState, reportId);
            },
        },

        props: {
            decorations(state) {
                return this.getState(state)?.decorations;
            },
        },

        view(editorView) {
            return createPluginView(editorView, reportId, yReferenceMarks);
        },
    });
}

/**
 * Get reference citation plugin state
 * @param {EditorState} state - ProseMirror editor state
 * @returns {Object} Plugin state
 */
export function getReferenceCitationPluginState(state) {
    return referenceCitationPluginKey.getState(state);
}

